/**
 * 🔐 SESIÓN DEL PANEL DE ADMINISTRACIÓN
 *
 * El guardia de `/admin` era decorativo: comprobaba que existiera
 * `localStorage.token` (bastaba con `localStorage.setItem('token','x')` para
 * entrar) y decidía el rol de administrador comparando `localStorage.user.email`
 * con una cadena, es decir, con un dato que el propio navegador puede editar.
 * Además el token era un ID token de Firebase que caduca a la hora y nunca se
 * refrescaba.
 *
 * Ahora:
 *   · la sesión la manda **Firebase Auth** (`onAuthStateChanged`, ver
 *     `hooks/useSesion.js`), que refresca el token por su cuenta;
 *   · el rol sale del documento **`usuarios/{uid}` de Firestore**, no del cliente;
 *   · `localStorage.user` se conserva SOLO como caché de presentación (nombre y
 *     correo de la cabecera, y el `uid` que usa `utils/preferenciasUsuario.js`
 *     mientras `auth.currentUser` aún no está resuelto). Nada concede permisos.
 *   · `localStorage.token` ya no se escribe. Se sigue borrando al cerrar sesión
 *     para limpiar el que dejaron las versiones anteriores.
 *
 * La protección real sigue siendo las reglas de seguridad de Firestore: esto
 * evita que el panel se abra sin sesión, no sustituye a las reglas.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/** Claves de sesión en `localStorage`. Son las únicas que borra el logout. */
export const CLAVES_SESION = ['token', 'user'];

/**
 * Lee `usuarios/{uid}` y lo combina con la cuenta de Firebase Auth.
 *
 * Devuelve `null` cuando la cuenta existe en Auth pero NO tiene ficha en
 * Firestore. Antes, ese caso se interpretaba en el login como "es el
 * administrador" y se le fabricaba `{ rol: 'admin' }`: cualquier cuenta de Auth
 * sin ficha entraba al panel con permisos totales.
 *
 * @throws si Firestore no responde — quien llama decide qué hacer.
 */
export async function cargarPerfilDeSesion(cuenta) {
  if (!cuenta?.uid) return null;

  const snap = await getDoc(doc(db, 'usuarios', cuenta.uid));
  if (!snap.exists()) return null;

  return {
    ...snap.data(),
    uid: cuenta.uid,
    email: cuenta.email || snap.data().email || ''
  };
}

/** ¿Este perfil de Firestore es administrador? */
export const esAdministrador = (perfil) => perfil?.rol === 'admin';

/** ¿La ficha de Firestore está desactivada? (`activo: false`) */
export const estaDesactivado = (perfil) => perfil?.activo === false;

/** Caché de presentación. No decide permisos. */
export function guardarCacheUsuario(perfil) {
  try {
    localStorage.setItem('user', JSON.stringify(perfil));
  } catch (error) {
    console.error('No se pudo guardar la copia local del usuario:', error);
  }
}

export function leerCacheUsuario() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

/**
 * Borra SOLO las claves de sesión.
 *
 * El logout hacía `localStorage.clear()` "por seguridad", y con eso se llevaba
 * por delante el tema oscuro, el estado del menú, la configuración de columnas
 * de la vista de respuestas y las plantillas guardadas en local
 * (`plantilla_word_*`, `plantilla_excel_*`, `plantilla_archivo_*`), que son la
 * única copia de una plantilla que no llegó a subirse al servidor.
 */
export function limpiarSesionLocal() {
  CLAVES_SESION.forEach((clave) => localStorage.removeItem(clave));
}
